import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  addVideoPost,
  getVideosFeed,
  getUserSettings,
  toggleVideoLike,
} from "../firebase";
import "../styles/Videos.css";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineVideoCamera,
  AiOutlineCloseCircle,
  AiOutlineEnvironment,
} from "react-icons/ai";
import { FaUserCircle } from "react-icons/fa";

const AREAS = ["Tiruchengode", "Sankagiri", "Kumarapalayam", "Pallipalayam"];


export default function Videos() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [videos, setVideos] = useState([]);
  const [activeArea, setActiveArea] = useState("All updates");
  const [userArea, setUserArea] = useState("");
  const [userPhoto, setUserPhoto] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [posting, setPosting] = useState(false);

  // 🔹 Load videos + user details
  useEffect(() => {
    (async () => {
      if (user?.uid) {
        const settings = await getUserSettings(user.uid);
        setUserArea(settings?.area || "");
        setUserPhoto(settings?.photoURL || user?.photoURL || "");
      }
      const feed = await getVideosFeed();
      setVideos(feed);
    })();
  }, [user]);

  

  // 🔹 Filter videos by area
  const filteredVideos = useMemo(() => {
    return activeArea === "All updates"
      ? videos
      : videos.filter((v) => v.area === activeArea);
  }, [videos, activeArea]);

  // 🔹 Upload to Cloudinary — return both URL & public_id
  const uploadToCloudinary = async (file) => {
    if (!file) return { secure_url: "", public_id: "" };

    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_preset", uploadPreset);
    fd.append("resource_type", "video");

    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`,
      { method: "POST", body: fd }
    );

    const data = await res.json();
    return {
      secure_url: data?.secure_url || "",
      public_id: data?.public_id || "",
    };
  };

  // 🔹 Create Video Post
  const handlePost = async () => {
    if (!file) {
      alert("Please select a video!");
      return;
    }

    if (!selectedArea) {
      alert("Please select a location before posting!");
      setShowLocationPicker(true);
      return;
    }

    setPosting(true);
    try {
      const settings = await getUserSettings(user.uid);
      const uploadResult = await uploadToCloudinary(file);

      const videoUrl = uploadResult.secure_url;
      const publicId = uploadResult.public_id;

      await addVideoPost(
        user.uid,
        caption,
        videoUrl,
        selectedArea,
        settings?.name || user?.displayName || "User",
        settings?.photoURL || userPhoto || user?.photoURL || "",
        publicId // ✅ store Cloudinary public_id in Firestore
      );

      setCaption("");
      setFile(null);
      setPreview(null);
      setSelectedArea("");
      setShowLocationPicker(false);
      setVideos(await getVideosFeed());
    } catch (err) {
      console.error("❌ Video post error:", err);
      alert("Failed to post. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  // 🔹 Select video file & preview
  const handleVideoChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setPreview(url);
    }
  };

  const removePreview = () => {
    setFile(null);
    setPreview(null);
  };

  

  // 🔹 Like video
  const handleLike = async (v) => {
    if (!user) {
      sessionStorage.setItem("redirectAfterLogin", "/videos");
      navigate("/login");
      return;
    }
    const liked = v.likes?.includes(user.uid);
    await toggleVideoLike(v.id, user.uid, liked);
    setVideos((prev) =>
      prev.map((x) =>
        x.id === v.id
          ? {
              ...x,
              likes: liked
                ? x.likes.filter((id) => id !== user.uid)
                : [...(x.likes || []), user.uid],
            }
          : x
      )
    );
  };

  // 🔹 Share video link
  const handleShare = async (v) => {
    const shareUrl = `${window.location.origin}/v?id=${v.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert("🔗 Video link copied to clipboard!");
      }
    } catch (err) {
      console.error("Share failed:", err);
    }
  };

  // 🔹 Autoplay videos when visible — but respect manual pause/mute
  useEffect(() => {
    const videosEl = document.querySelectorAll("video");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (!video.dataset.userPaused) video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.6 }
    );

    videosEl.forEach((v) => {
      v.addEventListener("pause", () => (v.dataset.userPaused = true));
      v.addEventListener("play", () => (v.dataset.userPaused = false));
      observer.observe(v);
    });

    return () => observer.disconnect();
  }, [videos]);

  // 🔊 Enable sound globally after first user interaction
  useEffect(() => {
    const enableSound = () => {
      document.querySelectorAll("video").forEach((v) => {
        v.muted = false;
        v.play().catch(() => {});
      });
      window.removeEventListener("click", enableSound);
      window.removeEventListener("touchstart", enableSound);
    };

    window.addEventListener("click", enableSound);
    window.addEventListener("touchstart", enableSound);

    return () => {
      window.removeEventListener("click", enableSound);
      window.removeEventListener("touchstart", enableSound);
    };
  }, []);

  return (
    <div className="feed-wrap">
      {/* ===== Composer ===== */}
      {user ? (
        <div className="composer">
          {userPhoto ? (
            <img
              src={userPhoto}
              className="composer-avatar"
              alt="Profile"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : (
            <FaUserCircle className="composer-avatar-icon" />
          )}

          <input
            className="composer-input"
            placeholder="Write a caption..."
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />

          <label className="composer-attach">
            <input hidden type="file" accept="video/*" onChange={handleVideoChange} />
            <AiOutlineVideoCamera size={20} />
          </label>

          <div
            className="composer-location"
            onClick={() => setShowLocationPicker(true)}
            style={{ cursor: "pointer", marginRight: "8px" }}
          >
            <AiOutlineEnvironment size={20} />
          </div>

          <button className="composer-post" onClick={handlePost} disabled={posting}>
            {posting ? "Posting..." : "Post"}
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <button
            className="composer-post"
            onClick={() => {
              sessionStorage.setItem("redirectAfterLogin", "/videos");
              navigate("/login");
            }}
            style={{
              borderRadius: "25px",
              padding: "10px 24px",
              fontSize: "15px",
              fontWeight: "500",
            }}
          >
            Sign In to Post
          </button>
        </div>
      )}

      {/* ✅ Video Preview */}
      {preview && (
        <div className="image-preview-box">
          <video src={preview} controls className="image-preview" />
          <button className="remove-preview-btn" onClick={removePreview}>
            <AiOutlineCloseCircle size={22} />
          </button>
        </div>
      )}

      {/* 📍 Location Picker Modal */}
      {showLocationPicker && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowLocationPicker(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "20px",
              width: "80%",
              maxWidth: "320px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ textAlign: "center", marginBottom: "10px" }}>
              Choose your post location
            </h4>
            {AREAS.map((a) => (
              <label
                key={a}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 0",
                  fontSize: "15px",
                }}
              >
                <input
                  type="radio"
                  value={a}
                  checked={selectedArea === a}
                  onChange={(e) => setSelectedArea(e.target.value)}
                  style={{ marginRight: "10px" }}
                />
                {a}
              </label>
            ))}
            <button
              onClick={() => {
                if (!selectedArea) {
                  alert("Please select a location to continue!");
                } else {
                  setShowLocationPicker(false);
                }
              }}
              style={{
                width: "100%",
                background: "#007bff",
                color: "white",
                border: "none",
                padding: "10px",
                borderRadius: "8px",
                marginTop: "10px",
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      )}

      {/* ===== Area Chips ===== */}
      <div className="chip-row">
        <button
          className={`chip ${activeArea === "All updates" ? "active" : ""}`}
          onClick={() => setActiveArea("All updates")}
        >
          All updates
        </button>
        {AREAS.map((a) => (
          <button
            key={a}
            className={`chip ${activeArea === a ? "active" : ""}`}
            onClick={() => setActiveArea(a)}
          >
            {a}
          </button>
        ))}
      </div>

      {/* ===== Feed List ===== */}
      <div className="feed-list">
        {filteredVideos.length === 0 && (
          <p style={{ textAlign: "center", color: "#6b7280", marginTop: "20px" }}>
            No videos yet. Be the first to share!
          </p>
        )}
        {filteredVideos.map((v) => {
          const likes = v.likes || [];
          const liked = user && likes.includes(user.uid);
          return (
            <article key={v.id} className="feed-card">
              <header className="feed-head">
                {v.userPhotoURL ? (
                  <img
                    src={v.userPhotoURL}
                    className="feed-avatar"
                    alt="User"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <FaUserCircle className="feed-avatar-icon" />
                )}
                <div>
                  <h4>{v.userName}</h4>
                  <small>{v.area} • Today</small>
                </div>
              </header>
              {v.caption && <p className="feed-text">{v.caption}</p>}
              {v.videoUrl && (
                <video
                  src={v.videoUrl}
                  className="video-player"
                  controls
                  loop
                  playsInline
                  preload="metadata"
                  autoPlay
                />
              )}
              
              <footer className="feed-actions">
                <button
                  className={`like-btn ${liked ? "liked" : ""}`}
                  onClick={() => handleLike(v)}
                >
                  ❤️ {likes.length || ""}
                </button>
                <button onClick={() => handleShare(v)}>↗ Share</button>
              </footer>
            </article>
          );
        })}
      </div>
    </div>
  );
}
